<template>
  <div class="h-100">
    <app-home />

    <!-- Dialog for general messages -->
    <custom-dialog
      v-model="dialog.show"
      :title="dialog.title"
      :text="dialog.text"
      :action="dialog.action"
    />

    <!-- Websocket disconnection dialog -->
    <connection-lost-dialog
      v-model="wsDisconnected"
      :reconnecting="wsReconnecting"
      :attempts="wsReconnectAttempts"
      @reconnect="attemptReconnect"
    />

    <!-- Terms of Service Dialog -->
    <to-s-dialog
      v-model="ToSDialog.show"
      :content="ToSContent"
      :is-update="isTosReacceptance"
      :current-version="$store.state.auth.currentTosVersion || ''"
      @accept="acceptTos"
      @decline="declineTos"
    />

    <!-- Subscription Dialog -->
    <v-dialog
      v-model="subscriptionDialog.show"
      max-width="1040"
      width="96vw"
      v-if="!isSharedLink() && isBillingEnabled"
    >
      <v-card class="billing-shell-card" flat>
        <PurchaseSubscription />
      </v-card>
    </v-dialog>

    <!-- Google One Tap prompt on login and for demo sessions -->
    <google-one-tap
      v-if="showGoogleOneTap"
      :key="googleOneTapKey"
      :reprompt-ms="60000"
    />
  </div>
</template>

<script>
import CustomDialog from "@/components/common/Dialog.vue";
import ConnectionLostDialog from "@/components/common/ConnectionLostDialog.vue";
import ToSDialog from "@/components/common/ToSDialog.vue";
import { createRgbVarsForThemes } from "@/js/utils.vuetify.css";
import { getPredictors, getUserSettings } from "@/js/api.schema";
import { isTosReacceptance, requiresTosAcceptance } from "@/js/utils.tos";

import * as wsSingle from "@/js/api.websocket.single";
import {
  disconnect as disconnectSriesDownload,
  openSeriesWebsocket as openDownloadWs
} from "@/js/api.series.download";
import AppHome from "@/views/Home";
import { mapGetters } from "vuex";
import Vue from "vue";
import PurchaseSubscription from "@/components/PurchaseSubscription.vue";
import GoogleOneTap from "@/plugins/auth/components/GoogleOneTap.vue";
import ToSString from "./ToS.js"; // if in same directory

export default {
  name: "App",
  components: {
    AppHome,
    ConnectionLostDialog,
    CustomDialog,
    GoogleOneTap,
    ToSDialog,
    PurchaseSubscription
  },
  data() {
    return {
      dialog: { show: false, title: "", text: "", action: null },
      wsDisconnected: false,
      wsReconnecting: false,
      wsReconnectAttempts: 0,
      ToSDialog: { show: false },
      subscriptionDialog: { show: false },
      subscriptionCheckInterval: null
    };
  },
  created() {
    // Connect to websocket, disconnect on page unload

    try {
      wsSingle.disconnect();
      disconnectSriesDownload();
    } catch (error) {
      // Handle the error here, you can log it or perform other actions.
      console.error("An error occurred:", error);
    }
  },
  mounted() {
    this.$root.$on("show-connection-dialog", () => {
      this.wsDisconnected = true;
    });

    createRgbVarsForThemes(this.$vuetify.theme.themes.light);

    // Slight delay to ensure all components are ready
    setTimeout(() => {
      if (this.isAuth) {
        this.$store.dispatch("billing/refreshBillingData");
        if (this.requiresTosAcceptance) {
          this.ToSDialog.show = true;
        }
      }
      if (this.isAuth && this.isBillingEnabled && !this.hasPaidSubscription) {
        this.subscriptionDialog.show = true;
      }

      // Set 5-minute interval
      this.subscriptionCheckInterval = setInterval(() => {
        if (this.isAuth) {
          this.$store.dispatch("billing/refreshBillingData");
        }
        if (this.isAuth && this.isBillingEnabled && !this.hasPaidSubscription) {
          this.subscriptionDialog.show = true;
        }
      }, 5 * 60 * 1000); // 5 minutes
    }, 500); // Adjust delay if needed
  },
  beforeDestroy() {
    if (this.subscriptionCheckInterval) {
      clearInterval(this.subscriptionCheckInterval);
      this.subscriptionCheckInterval = null;
    }
  },
  computed: {
    ...mapGetters("auth", ["isAuth"]),
    ...mapGetters("billing", ["isBillingEnabled"]),
    requiresTosAcceptance() {
      return requiresTosAcceptance({
        user: this.$store.state.auth.user,
        tosUpdateRequired: this.$store.state.auth.tosUpdateRequired
      });
    },
    isTosReacceptance() {
      return isTosReacceptance({
        user: this.$store.state.auth.user,
        tosUpdateRequired: this.$store.state.auth.tosUpdateRequired
      });
    },
    ToSContent() {
      return ToSString; // Ensure Vue tracks it
    },
    hasPaidSubscription() {
      return this.$store.state.auth.user?.subscription_tier !== "free";
    },
    showGoogleOneTap() {
      const isDemoUser = this.$store.state.auth.user?.is_demo === true;
      const isLoginRoute = this.$route?.name === "login";
      return isDemoUser || isLoginRoute;
    },
    googleOneTapKey() {
      const routeName = this.$route?.name || "no-route";
      const userId = this.$store.state.auth.user?.id || "anon";
      const isDemoUser = this.$store.state.auth.user?.is_demo ? "demo" : "std";
      return `${routeName}:${userId}:${isDemoUser}`;
    }
  },
  methods: {
    isSharedLink() {
      return !!this.$route.params.sharedId;
    },
    isDownloadEnabled() {
      return true; //return !this.isAuth;
    },
    isUploadEnabled() {
      return true; // return this.isAuth && !this.$isDemoUser;
    },
    connectWebsocket() {
      this.wsDisconnected = false;
      this.wsReconnecting = false;

      const onClose = () => {
        if (!this.wsDisconnected) {
          this.wsDisconnected = true;
        }
      };
      const onError = () => {
        if (!this.wsDisconnected) {
          this.wsDisconnected = true;
        }
      };

      const userId =
        (this.$store.state.auth.user && this.$store.state.auth.user.uuid) ||
        this.user;
      if (this.isDownloadEnabled())
        openDownloadWs(userId, onClose, onError, this.$router);
    },
    attemptReconnect() {
      this.wsReconnecting = true;
      this.wsReconnectAttempts++;

      // Disconnect existing sockets before reconnecting
      try {
      wsSingle.disconnect();
      disconnectSriesDownload();
      } catch (e) {
        // ignore cleanup errors
      }

      // Small delay to let the old connections close cleanly
      setTimeout(() => {
        this.wsDisconnected = false;
        this.wsReconnecting = false;
        this.connectWebsocket();
      }, 1000);
    },
    async loadPredictors() {
      // get all predictors
      const predictors = await getPredictors();
      this.$store.commit("viewer/initPredictors", predictors);
    },
    loadUserSettings() {
      getUserSettings()
        .then(data => {
          // update store values
          this.$store.commit("viewer/initOptions", data);
          if (data.show_ground_truth_panel !== undefined) {
            this.$store.commit(
              "viewer/setShowGroundTruthPanel",
              data.show_ground_truth_panel
            );
          }

          // update dashboard series display settings
          if (data.hide_for_processing_series !== undefined) {
            this.$store.commit(
              "dashboard/setHideForProcessingSeries",
              data.hide_for_processing_series
            );
          }
          if (data.group_proj_raw_series !== undefined) {
            this.$store.commit(
              "dashboard/setGroupProjRawSeries",
              data.group_proj_raw_series
            );
          }
        })
        .catch(error => console.error(error));
    },
    async acceptTos() {
      try {
        const locale = (navigator.language || "en").slice(0, 10);
        const versionFromStore =
          this.$store.state.auth.currentTosVersion ||
          this.$store.state.auth.user?.tos_version ||
          "legacy";
        const response = await Vue.$http.patch("auth/users/accept-tos", null, {
          accepted_tos: true,
          tos_version: versionFromStore,
          locale,
          source: "web_dialog"
        });
        const payload = response.body || response.data || {};
        const acceptedVersion = payload.current_tos_version || versionFromStore;

        // Update the user object in Vuex
        this.$store.commit("auth/updateUser", {
          accepted_tos: true,
          tos_version: acceptedVersion,
          tos_locale: locale,
          tos_accept_source: "web_dialog",
          tos_accepted_at: new Date().toISOString()
        });
        this.$store.commit("auth/update", {
          key: "tosUpdateRequired",
          value: false
        });
        this.$store.commit("auth/update", {
          key: "currentTosVersion",
          value: acceptedVersion
        });
        // Close the TOS dialog
        this.ToSDialog.show = false;
      } catch (error) {
        const payload = error.body || error.data || {};
        if (payload.current_tos_version) {
          this.$store.commit("auth/update", {
            key: "currentTosVersion",
            value: payload.current_tos_version
          });
        }
        console.error("Failed to update TOS acceptance status:", error);
      }
    },
    declineTos() {
      // Handle TOS decline (e.g., log out the user or show a warning)
      this.ToSDialog.show = false;
      this.$store.dispatch("auth/logout");
      this.$router.push("/login");
    }
  },

  watch: {
    isAuth(value) {
      this.$store.commit("viewer/initViewerSessionId");
      if (value) {
        this.loadPredictors();
        this.loadUserSettings();
        this.$store.dispatch("billing/refreshBillingData");
        this.connectWebsocket();

        // Show TOS dialog if the user hasn't accepted current TOS yet
        if (this.requiresTosAcceptance) {
          this.ToSDialog.show = true;
        }
      } else {
        this.$store.commit("billing/reset");
        this.subscriptionDialog.show = false;
      }
    },
    requiresTosAcceptance(value) {
      if (!this.isAuth) {
        this.ToSDialog.show = false;
        return;
      }
      this.ToSDialog.show = Boolean(value);
    },
    isBillingEnabled(value) {
      if (!value) {
        this.subscriptionDialog.show = false;
        return;
      }
      if (this.isAuth && !this.hasPaidSubscription) {
        this.subscriptionDialog.show = true;
      }
    }
  }
};
</script>

<style lang="scss">
@import "@/styles/utils.scss";
@import "@/styles/fonts.scss";
@import "@/styles/main.scss";

.billing-shell-card {
  background: #1e1e1e !important;
  border: 1px solid #3a3a3a;
  color: #f5f5f5 !important;
}
</style>
